/**
 * The studio board — `apps/api/src/readmodels/tasks-board.ts`, published as a shape
 * by session 20.
 *
 * The read model itself is session 07's and predates this directory; what it did not
 * have was a wire shape or a door. `mockups/studio/tasks.html`'s five headline
 * figures and its per-client columns are computed there and nowhere else
 * (`.docs/07-tasks.md` §DRY watchlist: "board counts are computed in the read model,
 * not by the SPA"), so the board page needs exactly this and `tasks_list` for the
 * cards themselves.
 *
 * The field names are the read model's own — `TasksBoard` in `tasks-board.ts` — so
 * the two cannot drift by a rename. `extras_in_flight` is a **list**, one entry per
 * currency, and that is deliberate: the board would otherwise print one number for
 * $470 and €1,780 at a rate nobody chose (`.docs/00-index.md` §6 converts only where
 * a rule says to).
 */
import { z } from 'zod';
import { MoneySchema } from '../primitives.js';
import { ClientSlugSchema } from '../refs.js';
export const TasksBoardCountsSchema = z
    .object({
    overdue: z
        .int()
        .describe('Past its due date and neither done nor cancelled. The board’s hero figure.'),
    blocked_on_client: z
        .int()
        .describe('Blocked with the next move theirs — the ochre rail on the board.'),
    open: z.int().describe('Everything in hand: neither done nor cancelled.'),
    due_this_week: z.int().describe('Due today or in the next six days, in the studio’s own zone.'),
    extras_in_flight: z
        .array(MoneySchema)
        .describe('The money on extras being delivered, one entry per currency and never added ' +
        'together — two currencies are two figures.'),
    extras_in_flight_tasks: z.int().describe('How many tasks that money is spread over.'),
})
    .describe('`studio/tasks.html`’s five headline figures, taken over the tasks that match the ' +
    'filters asked for, and over every task when none is. Under a filter, a studio ' +
    'account on the task-board tier also has the tasks its list hides left out. Extras ' +
    'in flight are money and are never narrowed by a filter.');
export const TasksBoardClientSchema = z
    .object({
    client: ClientSlugSchema.nullable().describe('Null for the studio’s own column: work that belongs to no client.'),
    name: z
        .string()
        .describe('The company, or "Internal - no client" for the column with no client.'),
    open: z.int().describe('How many tasks are in hand for them — the column’s own count.'),
    overdue: z.int().describe('How many of that column’s tasks are past their due date.'),
    blocked_on_client: z
        .int()
        .describe('How many of that column’s tasks are blocked waiting on this client.'),
    matching: z
        .int()
        .describe('How many of that column’s tasks match the filters. The columns add up to matching.'),
    past_due: z
        .boolean()
        .nullable()
        .describe('True when their retainer is past due — the pill on Vela Interiors’ column. Null ' +
        'means unknown rather than fine.'),
})
    .describe('One column of the board.');
export const TasksBoardOthersSchema = z
    .object({
    clients: z.int().describe('How many clients have a column that was not returned.'),
    open: z.int().describe('Their tasks in hand, together.'),
    overdue: z.int().describe('Their overdue tasks, together.'),
    blocked_on_client: z.int().describe('Their tasks blocked waiting on them, together.'),
    matching: z.int().describe('Their tasks that match the filters, together.'),
})
    .describe('The client columns max_columns left out, as one figure each.');
export const TasksBoardSchema = z
    .object({
    counts: TasksBoardCountsSchema,
    matching: z
        .int()
        .describe('How many tasks match the filters: the set tasks_list pages under the same ' +
        'filters, counted up to 10,000; past that, 10,000 with matching_capped true. ' +
        'With no filter, every task, done and cancelled included; for a studio account ' +
        'on the task-board tier, always without the tasks its list hides.'),
    matching_capped: z
        .boolean()
        .describe('True when more than 10,000 tasks match and matching stopped counting at 10,000. ' +
        'The columns’ own matching are exact either way.'),
    clients: z
        .array(TasksBoardClientSchema)
        .describe('One column per client, then the studio’s own, last, in name order. With no ' +
        'filter, the clients with work in hand; with any, the clients with a matching ' +
        'task. With max_columns, at most that many client columns, the ones with the ' +
        'most overdue and then the most open, and the rest summed in others.'),
    others: TasksBoardOthersSchema.nullable().describe('The client columns max_columns left out, summed. Null when max_columns was not ' +
        'sent, and every column is in clients.'),
})
    .describe('`studio/tasks.html`’s hero region and its columns, without the cards.');
//# sourceMappingURL=tasks.js.map